import React from "react";
import ReactDOM from "react-dom/client";

import { AppProviders } from "./AppProviders";
import { RouterAdapter } from "./navigation/RouterAdapter";
import { TenantScopeProvider } from "./contexts/TenantScopeProvider";

import reportWebVitals from "./reportWebVitals";
import { captureProviderReturnParams } from "./components/integrations/providerReturnParams";
import { claimAdmissionHandoff } from "./utils/admissionHandoff";
import { followSignOutElsewhere } from "./utils/logout";
import { setPageTitle } from "./util/elementUtil";
import { createPlatform, installPlatform } from "./platform/bootstrap";
import { designV2Override } from "./hooks/configuration";
import { tenantRoute } from "./utils/tenantRoute";
import { loadOverviewScreen, loadWorkspaceShell } from "./startupChunks";
import { resolveAppChrome } from "./navigation/appChrome";
import { tabletShellAtStartup } from "./hooks/useTabletShell";
import { isInIframe } from "./utils/browserUtils";

import BootstrapShell from "./components/BootstrapShell";

function ProjectAdmission({ children }: { children: React.ReactNode }) {
    return (
        <RouterAdapter>
            <TenantScopeProvider>{children}</TenantScopeProvider>
        </RouterAdapter>
    );
}

async function main(): Promise<void> {
    // Strip OAuth credentials before platform initialization, lazy app imports,
    // rendering, or analytics can observe the SharePoint callback URL.
    captureProviderReturnParams();
    // A project switch's credential leaves storage on every Document, whatever its route.
    claimAdmissionHandoff();
    // A sign-out in another tab of this browser ends this tab's part of the session too.
    followSignOutElsewhere();

    const platform = await createPlatform();
    installPlatform(platform);
    // While the person uses the app, the web's sign-in slides to 30 days, once a day. Its own
    // chunk, off the first load: it is due at most daily, and a load that fails leaves the
    // session as it was until the next page load tries again.
    void import("./auth/sessionRenewal")
        .then(({ installSessionRenewal }) => installSessionRenewal(platform))
        .catch(() => undefined);
    // Start loading immediately, alongside admission. Modules in this graph read
    // platformRef on evaluation, so installation must precede the import.
    const appModule = import("./App");
    const App = React.lazy(() => appModule);
    // The shell and Home chunks start beside it rather than once App has rendered, by App's own
    // chrome rule: the tablet shell (always on iOS) uses neither. A failure here is the lazy
    // component's to meet again. The project's design is known only once its flag answers, after
    // admission (`DesignV2Gate`), so the Design v2 shell starts unless this browser chose classic:
    // a v2 page waits on it, a classic one leaves it unused.
    const { pathname } = tenantRoute();
    const chrome = resolveAppChrome({
        pathname,
        redesigned: designV2Override() ?? true,
        tablet: tabletShellAtStartup(platform),
        inIframe: isInIframe(),
    });
    if (chrome === "workspace") void loadWorkspaceShell().catch(() => undefined);
    if (chrome !== "tablet" && pathname === "/") void loadOverviewScreen().catch(() => undefined);

    setPageTitle(undefined);

    const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

    root.render(
        // <React.StrictMode>
        <AppProviders platform={platform} admissionBoundary={ProjectAdmission}>
            <React.Suspense fallback={<BootstrapShell />}>
                <App />
            </React.Suspense>
        </AppProviders>,
        // </React.StrictMode>
    );

    reportWebVitals();
}

void main();
